
import React from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { BlogForm } from '@/components/admin/blog/BlogForm';
import { usePermissions } from '@/contexts/unified-permissions';
import { useAdminCheck } from '@/components/admin/blog/hooks/useAdminCheck';
import { LoadingState } from '@/components/ui/loading-state';
import { BlogService } from '@/services/blog/blogService';
import { toast } from 'sonner';

interface BlogFormData {
  title?: string;
  slug?: string;
  content?: string;
  summary?: string;
  tags?: string[];
  published?: boolean;
  meta_title?: string;
  meta_description?: string;
  author_name?: string;
  featured_image?: string;
}

const BlogCreateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePermissions();
  const { isAdmin, isLoading } = useAdminCheck();
  const isEditing = Boolean(id);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoadingPost, setIsLoadingPost] = React.useState(false);

  const form = useForm<BlogFormData>({
    defaultValues: {
      title: '',
      slug: '',
      content: '',
      summary: '',
      tags: [],
      published: false,
      meta_title: '',
      meta_description: '',
      author_name: 'The Mat Monster Team',
      featured_image: '',
    },
  });

  // Fetch post data when editing
  React.useEffect(() => {
    const fetchPost = async () => {
      if (isEditing && id) {
        setIsLoadingPost(true);
        try {
          const post = await BlogService.getPostById(id);
          if (post) {
            // Reset form with the fetched post data
            form.reset({
              title: post.title || '',
              slug: post.slug || '',
              content: post.content || '',
              summary: post.summary || '',
              tags: post.tags || [],
              published: post.published || false,
              meta_title: post.meta_title || '',
              meta_description: post.meta_description || '',
              author_name: post.author_name || 'The Mat Monster Team',
              featured_image: post.featured_image || '',
            });
          } else {
            toast.error('Blog post not found');
            navigate('/blog/admin');
          }
        } catch (error) {
          console.error('Error fetching blog post:', error);
          toast.error('Failed to load blog post');
          navigate('/blog/admin');
        } finally {
          setIsLoadingPost(false);
        }
      }
    };

    fetchPost();
  }, [id, isEditing, form, navigate]);

  const handleSubmit = async (data: BlogFormData) => {
    setIsSubmitting(true);
    try {


      // Prepare the blog post data
      const postData = {
        title: data.title || '',
        slug: data.slug || '',
        content: data.content || '',
        summary: data.summary || '',
        featured_image: data.featured_image || '',
        tags: data.tags || [],
        published: data.published || false,
        meta_title: data.meta_title || data.title || '',
        meta_description: data.meta_description || data.summary || '',
        author_name: data.author_name || 'The Mat Monster Team',
      };

      if (isEditing && id) {
        // Update existing post
        await BlogService.updatePost(id, postData);
        toast.success('Blog post updated successfully!');
      } else {
        // Create new post
        await BlogService.createPost(postData);
        toast.success('Blog post created successfully!');
      }

      // Navigate back to blog admin
      navigate('/blog/admin');
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast.error('Failed to save blog post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingState title="Checking permissions..." />;
  }

  if (isLoadingPost) {
    return <LoadingState title="Loading blog post..." />;
  }

  if (!user) {
    return <div>Please log in to access this page.</div>;
  }

  if (!isAdmin) {
    return <div>Access denied. Admin permissions required.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8">
        {isEditing ? 'Edit Blog Post' : 'Create Blog Post'}
      </h1>
      <BlogForm
        form={form}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        mode={isEditing ? 'edit' : 'create'}
      />
    </div>
  );
};

export default BlogCreateEdit;
